import {
  normalizePage,
  PAYMENTS_SERVICE_BASE_URL,
  Page,
  serviceFetch,
  serviceJson,
  CLIENT_APP,
  CLIENT_STAGES,
} from './mobileParityApi';

export type PaymentCard = {
  id: string;
  userId?: string;
  cardNumber: string;
  cardHolderName?: string;
  cardNickname?: string;
  expiryDate?: string;
  status?: string;
  cardType?: string;
  cardMerchant?: string;
  defaultIncomingPaymentMethod?: boolean;
  defaultOutgoingPaymentMethod?: boolean;
};

export type BankAccount = {
  id: string;
  userId?: string;
  accountNumber: string;
  routingNumber?: string;
  bankName?: string;
  accountName?: string;
  accountNickname?: string;
  status?: string;
  defaultIncomingPaymentMethod?: boolean;
  defaultOutgoingPaymentMethod?: boolean;
};

export type StripePaymentMethod = {
  id: string;
  stripePaymentMethodId?: string;
  merchant?: string;
  last4?: string;
  expiryMonth?: number;
  expiryYear?: number;
  status?: string;
  defaultIncomingPaymentMethod?: boolean;
  defaultOutgoingPaymentMethod?: boolean;
};

export type StripeExternalPayoutAccount = {
  id: string;
  bankName?: string | null;
  brand?: string | null;
  last4: string;
  country?: string | null;
  currency?: string | null;
  isDefault?: boolean;
  isDefaultPaymentAccount?: boolean;
};

export type StripeConnectAccount = {
  id?: string;
  stripeAccountId?: string;
  status?: string;
  type?: string;
  email?: string;
  country?: string;
  onboardingComplete?: boolean;
  onboarded?: boolean;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  currentlyDue?: string[];
  eventuallyDue?: string[];
  pastDue?: string[];
  pendingVerification?: string[];
  externalAccounts?: StripeExternalPayoutAccount[];
};

export type PaymentChargeThinDTO = {
  id: string;
  description?: string | null;
  chargeItemCode?: string | null;
  amount?: number | null;
  receivableAmount?: number | null;
  chargeableAmount?: number | null;
  appliedCreditsTotal?: number | null;
  currency?: string | null;
  status?: string | null;
  paymentStatus?: string | null;
  createdAt?: string | null;
  paymentMethod?: {
    brand?: string | null;
    last4?: string | null;
    bankName?: string | null;
  } | null;
  paymentChargeDetails?: Record<string, unknown> | null;
};

export type StripeTransferDTO = {
  id: string;
  stripeTransferId?: string | null;
  amount?: number | null;
  currency?: string | null;
  totalAmount?: number | null;
  totalCharges?: number | null;
  netPayout?: number | null;
  transferredAt?: string | null;
  status?: string | null;
  createdAt?: string | null;
  payoutCharges?: Array<Record<string, unknown>>;
};

export type ChargeUserDTO = {
  id: string;
  description?: string | null;
  chargeItemId?: string | null;
  chargeItemCode?: string | null;
  amount?: number | null;
  receivableAmount?: number | null;
  chargeableAmount?: number | null;
  currency?: string | null;
  status?: string | null;
  paymentStatus?: string | null;
  createdAt?: string | null;
  paymentMethod?: {
    merchant?: string | null;
    brand?: string | null;
    last4?: string | null;
  } | null;
  user?: {
    id?: string;
    email?: string | null;
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
  } | null;
  paymentChargeDetails?: Record<string, unknown> | null;
};

export async function fetchStripePaymentMethods(
  actorId?: string,
  page = 0,
  size = 10
): Promise<StripePaymentMethod[]> {
  const raw = await serviceJson<Page<StripePaymentMethod>>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/methods?page=${page}&size=${size}`,
    { method: 'GET', actorId, acceptJson: true },
    'Failed to fetch Stripe payment methods'
  );
  return normalizePage(raw).content;
}

export async function fetchPaymentCards(actorId?: string): Promise<PaymentCard[]> {
  const raw = await serviceJson<PaymentCard[]>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payment-cards/user`,
    { method: 'GET', actorId, acceptJson: true },
    'Failed to fetch payment cards'
  );
  return Array.isArray(raw) ? raw : [];
}

export async function fetchBankAccounts(actorId?: string): Promise<BankAccount[]> {
  const raw = await serviceJson<BankAccount[]>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/bank-accounts/user`,
    { method: 'GET', actorId, acceptJson: true },
    'Failed to fetch bank accounts'
  );
  return Array.isArray(raw) ? raw : [];
}

export async function setDefaultOutgoingPaymentMethod(
  methodId: string,
  actorId?: string
): Promise<void> {
  await serviceFetch(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/method/${encodeURIComponent(methodId)}/set-default-outgoing`,
    { method: 'PUT', actorId, json: false },
    'Failed to set default outgoing payment method'
  );
}

export async function deletePaymentMethod(methodId: string, actorId?: string): Promise<void> {
  await serviceFetch(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/method/${encodeURIComponent(methodId)}`,
    { method: 'DELETE', actorId, json: false },
    'Failed to delete payment method'
  );
}

export async function createStripePaymentMethod(
  paymentMethodId: string,
  actorId?: string
): Promise<StripePaymentMethod | null> {
  if (!paymentMethodId) throw new Error('Invalid payment method id');

  return serviceJson<StripePaymentMethod | null>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/attach-method`,
    {
      method: 'POST',
      actorId,
      body: JSON.stringify({ paymentMethodId }),
    },
    'Failed to attach payment method to your account'
  );
}

export async function fetchStripeConnectAccount(
  uid?: string,
  refresh?: boolean
): Promise<StripeConnectAccount | null> {
  const params = new URLSearchParams();
  if (uid) params.set('uid', uid);
  if (typeof refresh === 'boolean') params.set('refresh', String(refresh));

  return serviceJson<StripeConnectAccount | null>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/connect-account${params.toString() ? `?${params}` : ''}`,
    { method: 'GET', acceptJson: true },
    'Failed to fetch Stripe connect account'
  );
}

export async function createExternalPayoutAccount(
  payload: { externalAccountToken: string },
  actorId?: string
): Promise<StripeExternalPayoutAccount> {
  return serviceJson<StripeExternalPayoutAccount>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/add-payout-account`,
    {
      method: 'POST',
      actorId,
      body: JSON.stringify({ externalAccountToken: payload.externalAccountToken }),
    },
    'Failed to create external payout account'
  );
}

export async function fetchExternalPayoutAccounts(
  actorId?: string
): Promise<StripeExternalPayoutAccount[]> {
  const raw = await serviceJson<StripeExternalPayoutAccount[]>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/external/accounts/active`,
    { method: 'GET', actorId, acceptJson: true },
    'Failed to fetch payout accounts'
  );
  return Array.isArray(raw) ? raw : [];
}

export async function setDefaultExternalPayoutAccount(
  accountId: string,
  actorId?: string
): Promise<void> {
  await serviceFetch(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/external-account/${encodeURIComponent(accountId)}/set-default`,
    { method: 'PUT', actorId, json: false },
    'Failed to set default external payout account'
  );
}

export async function deleteExternalPayoutAccount(
  accountId: string,
  actorId?: string
): Promise<void> {
  await serviceFetch(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/external-payout-account/${encodeURIComponent(accountId)}`,
    { method: 'DELETE', actorId, json: false },
    'Failed to delete external payout account'
  );
}

export async function createHelperExpressOnboardingLink(
  userId: string,
  actorId?: string
): Promise<{ url: string }> {
  return serviceJson<{ url: string }>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/stripe-connect/helper/${encodeURIComponent(userId)}/onboard`,
    { method: 'POST', actorId },
    'Failed to create Stripe Express onboarding link'
  );
}

export async function createHelperExpressDashboardLink(
  userId: string,
  actorId?: string
): Promise<{ url: string }> {
  return serviceJson<{ url: string }>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/stripe-connect/helper/${encodeURIComponent(userId)}/dashboard`,
    { method: 'POST', actorId },
    'Failed to create Stripe Express dashboard link'
  );
}

export async function fetchReceivablePaymentActivities(
  page = 0,
  size = 10
): Promise<PaymentChargeThinDTO[]> {
  const now = new Date();
  const sixMonthsAgo = new Date(now);
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  const params = new URLSearchParams({
    start: sixMonthsAgo.toISOString(),
    end: now.toISOString(),
    page: String(page),
    size: String(size),
    sort: 'createdAt,desc',
  });

  const raw = await serviceJson<PaymentChargeThinDTO[] | Page<PaymentChargeThinDTO>>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/charges/receivable/user/range?${params}`,
    { method: 'GET', acceptJson: true },
    'Failed to fetch receivable payment activities'
  );
  return normalizePage(raw).content;
}

export async function fetchPayoutActivitiesReceived(
  userId: string
): Promise<StripeTransferDTO[]> {
  const raw = await serviceJson<StripeTransferDTO[] | Page<StripeTransferDTO>>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/payout/user/${encodeURIComponent(userId)}/received`,
    { method: 'GET', acceptJson: true },
    'Failed to fetch payout activities'
  );
  return normalizePage(raw).content;
}

export type AvailableEarningCharge = {
  id: string;
  receivableAmount?: number | null;
  amount?: number | null;
  currency?: string | null;
  status?: string | null;
};

export type EffectiveChargeConfigDTO = {
  id: string;
  code?: string;
  name?: string;
  category?: string;
  valueType?: string;
  value?: number;
  applyOn?: string;
  displayOrder?: number;
  chargeTarget?: string;
  currency?: string;
  country?: string;
  state?: string;
  county?: string;
  city?: string;
  minAmount?: number;
  maxAmount?: number;
};

export async function fetchAvailableEarningCharges(actorId?: string): Promise<AvailableEarningCharge[]> {
  const raw = await serviceJson<AvailableEarningCharge[]>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/earnings/charges?status=COMPLETED`,
    { method: 'GET', actorId, acceptJson: true },
    'Failed to fetch available earnings'
  );
  return Array.isArray(raw) ? raw : [];
}

export async function fetchDefaultPayoutAccount(actorId?: string): Promise<StripeExternalPayoutAccount | null> {
  return serviceJson<StripeExternalPayoutAccount | null>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/external-account/default`,
    { method: 'GET', actorId, acceptJson: true },
    'Failed to fetch default payout account'
  );
}

export async function fetchEffectivePayoutCharges(params: {
  amount: number;
  country?: string;
  state?: string;
  county?: string;
  city?: string;
}): Promise<EffectiveChargeConfigDTO[]> {
  const search = new URLSearchParams({
    app: CLIENT_APP,
    currency: 'USD',
    stages: CLIENT_STAGES,
    chargeTarget: 'PAYOUT',
    amount: String(params.amount),
    page: '0',
    size: '20',
  });
  if (params.country) search.set('country', params.country);
  if (params.state) search.set('state', params.state);
  if (params.county) search.set('county', params.county);
  if (params.city) search.set('city', params.city);

  const raw = await serviceJson<Page<EffectiveChargeConfigDTO>>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/charges/effective/paged?${search}`,
    { method: 'GET', acceptJson: true },
    'Failed to fetch payout charge configuration'
  );
  return normalizePage(raw).content;
}

const payoutChargeAmount = (grossAmount: number, charge: EffectiveChargeConfigDTO) => {
  if (!grossAmount || grossAmount <= 0 || !charge.value || charge.value <= 0) return 0;
  const valueType = String(charge.valueType || '').toUpperCase();
  const value = Number(charge.value);
  return valueType === 'PERCENTAGE' ? grossAmount * value : value;
};

export async function submitEarningsCashOut(
  payload: {
    chargeIds: string[];
    currency: string;
    totalAmount: number;
    totalCharges: number;
    netPayout: number;
    charges: Array<Record<string, unknown>>;
  },
  actorId?: string
): Promise<void> {
  await serviceFetch(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/earnings/cashout`,
    {
      method: 'POST',
      actorId,
      body: JSON.stringify(payload),
    },
    'Failed to process cash out'
  );
}

export function buildCashOutChargePayload(charges: EffectiveChargeConfigDTO[], grossAmount: number) {
  return [...charges]
    .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0))
    .map((charge) => ({
      chargeId: charge.id,
      code: charge.code,
      name: charge.name || charge.code || 'Payout Charge',
      category: charge.category,
      valueType: charge.valueType,
      value: charge.value,
      applyOn: charge.applyOn,
      displayOrder: charge.displayOrder,
      chargeTarget: charge.chargeTarget,
      currency: charge.currency,
      country: charge.country,
      state: charge.state,
      county: charge.county,
      city: charge.city,
      minAmount: charge.minAmount,
      maxAmount: charge.maxAmount,
      amount: payoutChargeAmount(grossAmount, charge),
    }));
}

export async function fetchUserChargesRange(params: {
  userId?: string;
  startISO: string;
  endISO: string;
  page?: number;
  size?: number;
}): Promise<Page<ChargeUserDTO>> {
  const search = new URLSearchParams({
    start: params.startISO,
    end: params.endISO,
    page: String(params.page ?? 0),
    size: String(params.size ?? 50),
    sort: 'createdAt,desc',
  });
  if (params.userId) search.set('uid', params.userId);

  const raw = await serviceJson<ChargeUserDTO[] | Page<ChargeUserDTO>>(
    `${PAYMENTS_SERVICE_BASE_URL}/api/v1/payments/charges/user/range?${search}`,
    { method: 'GET', acceptJson: true },
    'Failed to fetch outgoing payment charges'
  );
  const normalized = normalizePage(raw);
  return {
    ...normalized,
    content: normalized.content.filter(Boolean),
  };
}
